"use client";

import { cn } from "@/lib/utils";

/**
 * An iPhone-style device frame with notch, status bar and home indicator.
 * The screen content is supplied via {@link children} and fills the inner area.
 */
export function PhoneFrame({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative h-[600px] w-[296px] rounded-[2.8rem] border border-brand-800 bg-brand-950 p-3 shadow-2xl shadow-brand-900/40",
        className,
      )}
    >
      {/* Subtle bezel highlight */}
      <div className="pointer-events-none absolute inset-0 rounded-[2.8rem] ring-1 ring-inset ring-white/5" />

      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[2.1rem] bg-white">
        {/* Notch */}
        <div className="absolute left-1/2 top-0 z-30 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-brand-950" />

        {/* Status bar */}
        <div className="relative z-20 flex shrink-0 items-center justify-between px-6 pb-1 pt-3 text-[10px] font-semibold text-brand-900">
          <span>9:41</span>
          <span className="flex items-center gap-1">
            <span className="flex items-end gap-[2px]">
              <span className="h-1.5 w-[3px] rounded-sm bg-brand-900/70" />
              <span className="h-2 w-[3px] rounded-sm bg-brand-900/70" />
              <span className="h-2.5 w-[3px] rounded-sm bg-brand-900" />
            </span>
            <span className="ml-0.5 h-2.5 w-4 rounded-[3px] border border-brand-900/60">
              <span className="block h-full w-2/3 rounded-[2px] bg-brand-900/70" />
            </span>
          </span>
        </div>

        {/* Screen content */}
        <div className="relative flex-1 overflow-hidden">{children}</div>

        {/* Home indicator */}
        <div className="flex shrink-0 justify-center py-2">
          <div className="h-1 w-24 rounded-full bg-brand-900/20" />
        </div>
      </div>
    </div>
  );
}
